import React, { createContext, useContext, useState, useCallback } from 'react';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

// Create Delete Confirmation Context
const DeleteConfirmationContext = createContext();

// Delete Confirmation Provider Component
export const DeleteConfirmationProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [onConfirm, setOnConfirm] = useState(null); // The callback function when user confirms deletion
  const [itemName, setItemName] = useState(''); // Name of the item to display in the confirmation message

  // Show delete confirmation modal
  const showDeleteConfirmation = useCallback((name, confirmCallback) => {
    setItemName(name);
    setOnConfirm(() => confirmCallback); // Set the confirm callback
    setIsOpen(true);
  }, []);

  // Hide delete confirmation modal
  const hideDeleteConfirmation = useCallback(() => {
    setIsOpen(false);
    setItemName('');
    setOnConfirm(null);
  }, []);

  // Confirm deletion
  const handleConfirm = useCallback(() => {
    if (onConfirm) {
      onConfirm(); // Call the passed callback to handle the deletion
    }
    hideDeleteConfirmation();
  }, [onConfirm, hideDeleteConfirmation]);

  const value = {
    showDeleteConfirmation,
    hideDeleteConfirmation,
    handleConfirm,
  };

  return (
    <DeleteConfirmationContext.Provider value={value}>
      {children}

      {/* Render the delete confirmation modal */}
      {isOpen && (
        <DeleteConfirmationDialog
          open={isOpen}
          onClose={hideDeleteConfirmation}
          onConfirm={handleConfirm}
          itemName={itemName}
        />
      )}
    </DeleteConfirmationContext.Provider>
  );
};

// Custom hook to use Delete Confirmation
export const useDeleteConfirmation = () => {
  const context = useContext(DeleteConfirmationContext);
  if (!context) {
    throw new Error('useDeleteConfirmation must be used within DeleteConfirmationProvider');
  }
  return context;
};

export default DeleteConfirmationContext;
